var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * CZNA-510 — the compiled forms one ontology class has to be compared against.
 *
 * <p>Two rounds, because a class does not know its templates until it knows what
 * templates exist: one request lists every form-config the tenant has, and only then
 * can the EFCs of the relevant ones be compiled. The second round fans out over a
 * count that changes when the reader picks another class, which is what `useQueries`
 * is for — a fixed list of `useQuery` calls would break the rules of hooks.
 *
 * <p><b>The type list comes from the templates that exist, not from
 * `classDerivation`.</b> `AUTHOR_WORK` ships a form under a `planned` rule (and
 * `OLD_PRINT_CITY` had no rule at all until CZNA-658), so the ontology axis alone does
 * not know every shipped form — and the class most affected is `AdminMetadata`, whose
 * whole claim is that it is carried by every record.
 * {@link recordTypesForClass} takes the available types for exactly this reason.
 *
 * <p><b>Every template, not just `default`.</b> Comparing against the default alone
 * would report SUBJECT's `550` as "missing from every form" while NK's own complete
 * template carries it (CZNA-450). "Missing" has to mean missing from all of them, or
 * the amber list is an artefact of which template happened to be looked at.
 *
 * <p><b>Cost.</b> One unfiltered list plus one EFC per relevant template. An entity
 * class is 1 + 1..3; `AdminMetadata` is the worst case at 1 + one per shipped template
 * (11 today). The earlier shape asked per type and would have made that 9 + 9 — the
 * create dialog's own sweep and then some, which is precisely the trade its comment
 * said it was avoiding. `staleTime` is an hour and the unfiltered list is the same
 * cache slot the create dialog fills, so in practice the reader pays for the EFCs.
 *
 * <p><b>The query keys come from the existing factories</b>
 * (`formConfigTemplatesQueryKey`, `effectiveFormConfigQueryKey`) rather than from
 * literals repeated here. Same endpoint, same payload, so sharing the cache slot is
 * right — and a re-typed key is what drifts out of sync with an invalidation path
 * (`moduleSettingInvalidationKeys` invalidates both by those factories when a template
 * is saved, so the hour-long `staleTime` is not a staleness hazard after an NK edit).
 * The differing `staleTime` on a shared key is safe in react-query v3: it is read from
 * the observer's options, never written onto the shared `Query`.
 *
 * <p><b>A failed fetch stays `undefined`, never `{ tabs: [] }`.</b> An empty form and
 * an unread form are the same shape and opposite claims — the first says "the
 * cataloguer sees none of this", the second says "we do not know". {@link
 * buildOntologyFormMap} drops an undefined config, so a partial outage degrades the
 * map to `formsResolved: false` instead of manufacturing findings out of a 500.
 *
 * <p>MEMORY rule: `useOkapiKy()` returns an unstable reference. It is captured in the
 * query closures and NEVER listed in a dependency array — listing it is an infinite
 * refetch loop.
 */
import { useMemo } from 'react';
import { useOkapiKy } from '@folio/stripes/core';
import { useQueries, useQuery } from 'react-query';
import { API_PATHS, CZNA_MODULE_SETTINGS_EFFECTIVE_TEMPLATE } from '../types/apiPaths';
import { recordTypesForClass } from '../settings/ontologyFormMap';
import { formConfigTemplatesQueryKey } from './useFormConfigTemplates';
import { effectiveFormConfigQueryKey } from './useFormConfig';
/** Same cache policy as `useSemanticCatalog` — both sides change only on deploy. */
var CACHE = {
    staleTime: 60 * 60 * 1000,
    cacheTime: 2 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
};
var useOntologyFormMapSources = function (catalog, className) {
    var _a, _b;
    // Closure-capture only. MEMORY: ky is unstable — never in deps.
    var ky = useOkapiKy();
    var allTemplates = useQuery(formConfigTemplatesQueryKey(undefined), function () { return ky.get(API_PATHS.CZNA_MODULE_SETTINGS_FORM_CONFIGS).json(); }, CACHE);
    var templates = (_b = (_a = allTemplates.data) === null || _a === void 0 ? void 0 : _a.templates) !== null && _b !== void 0 ? _b : [];
    var availableRecordTypes = useMemo(function () { return Array.from(new Set(templates.map(function (template) { return template.recordType; }))).sort(); }, 
    // On the DATA, not on the query object: react-query returns a new result each render.
    [allTemplates.dataUpdatedAt]);
    /**
     * Live and planned both, because the forward status only asks whether some shipped
     * form offers the tag — see {@link buildOntologyFormMap}, which keeps the two apart
     * again for the reverse gap.
     */
    var recordTypes = useMemo(function () {
        if (!className)
            return [];
        var _a = recordTypesForClass(catalog, className, availableRecordTypes), types = _a.types, planned = _a.planned;
        return Array.from(new Set(__spreadArray(__spreadArray([], types, true), planned, true))).sort();
    }, [catalog, className, availableRecordTypes]);
    /**
     * `(type, templateId)` pairs to compile. Recomputed every render on purpose:
     * `useQueries` de-duplicates by query key, so a fresh options array is normal usage
     * and costs a `matchQueries` pass. Memoizing it would need the query results in the
     * dependency list, and react-query hands back a new result object each render.
     */
    var pairs = templates
        .filter(function (template) { return recordTypes.includes(template.recordType); })
        .map(function (template) { return ({ recordType: template.recordType, templateId: template.templateId }); });
    var configs = useQueries(pairs.map(function (_a) {
        var recordType = _a.recordType, templateId = _a.templateId;
        return (__assign({ queryKey: effectiveFormConfigQueryKey(recordType, templateId), queryFn: function () { return ky
                .get(CZNA_MODULE_SETTINGS_EFFECTIVE_TEMPLATE(recordType, templateId))
                .json(); } }, CACHE));
    }));
    var forms = pairs.map(function (_a, index) {
        var _b;
        var recordType = _a.recordType, templateId = _a.templateId;
        return ({
            recordType: recordType,
            templateId: templateId,
            // `undefined` on failure — see the header. Never coerced to an empty form.
            config: (_b = configs[index]) === null || _b === void 0 ? void 0 : _b.data,
        });
    });
    return {
        forms: forms,
        recordTypes: recordTypes,
        availableRecordTypes: availableRecordTypes,
        isLoading: allTemplates.isLoading || configs.some(function (query) { return query.isLoading; }),
        isPartial: allTemplates.isError || configs.some(function (query) { return query.isError; }),
    };
};
export default useOntologyFormMapSources;
